import { useEffect, useMemo } from 'react';

import { getOpParams } from 'utils/getOpParams';
import { checkMasterModeAccess } from 'new-components/OrgSwitcher/helpers/orgAccess';
import { sharedRoutes } from 'new-components/OpAppScaffold/constants';
import { checkAuthIsUPCPartnerOrg } from 'utils/checkAuthIsUPCPartnerOrg';
import { useAppContext } from 'new-components/OpAppScaffold/AppContext';
import { useIdentityAndUser } from './useIdentityAndUser';
import { setWindowLocation } from '../../../altaLoginApp/helpers/setWindowLocation';
import { useOpQuery } from './useOpQuery';

export const useCheckOrgAccess = (basePath: string) => {
  const { orgId } = getOpParams();

  const { token } = useIdentityAndUser();

  // Check if user has mastermode access
  const hasMasterModeAccess = useMemo(
    () =>
      checkMasterModeAccess({
        token,
      }),
    [token],
  );

  // Calling describeOrg to check user access to the org. Need to call this anyway to check for access
  // thru parent org. But this already covers both parent and child user access.
  // If user can't call describeOrg, they don't have access to the org
  const { data: orgData, isLoading: isLoadingOrgData } = useOpQuery({
    apiEndpointName: 'describeOrg',
    parameters: [orgId],
    suppressErrorMessage: true,
    select: (heliumResponse) => heliumResponse?.json?.data,
  });

  const { app } = useAppContext();

  useEffect(() => {
    if (hasMasterModeAccess || !token || !orgId || isLoadingOrgData) return;

    // If user has orgData, they have access to the org
    if (!orgData) {
      setWindowLocation(`${basePath}${sharedRoutes.pageNotFound}`);
      return;
    }

    const currentOrgScope = token?.tokenScopeList?.find(
      (tokenScope) => tokenScope.org?.id === orgId,
    );

    if (app === 'partnerCenter' && !checkAuthIsUPCPartnerOrg(currentOrgScope)) {
      setWindowLocation(`${basePath}${sharedRoutes.pageNotFound}`);
    }
  }, [
    hasMasterModeAccess,
    token,
    orgId,
    app,
    basePath,
    isLoadingOrgData,
    orgData,
  ]);
};
